import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import { Unit, CreateUnitInput, UpdateUnitInput, DeleteUnitInput, ExistsResponse, SuccessResponse, UUID } from './types';

export const unitQueryKeys = {
  unit: 'UNIT',
  unitExists: 'UNIT_EXISTS',
  unitsByDocument: 'UNITS_BY_DOCUMENT',
} as const;

export function useUnit(unitId: UUID | undefined): UseQueryResult<Unit | null> {
  return useQuery(
    [unitQueryKeys.unit, unitId],
    async () => {
      const res = await request.get(`/gaby/units/${unitId}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as Unit) || null;
    },
    {
      enabled: !!unitId,
      onError: ServerErrorHandler,
    }
  );
}

export function useUnitExists(unitId: UUID | undefined): UseQueryResult<ExistsResponse> {
  return useQuery(
    [unitQueryKeys.unitExists, unitId],
    async () => {
      const res = await request.get(`/gaby/units/${unitId}/exists`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return { data: { exists: false } };
        }
        throw err;
      });
      return (res?.data as ExistsResponse) || { exists: false };
    },
    {
      enabled: !!unitId,
      onError: ServerErrorHandler,
    }
  );
}

export function useUnitsByDocument(documentSuperId: number | undefined): UseQueryResult<Unit[]> {
  return useQuery(
    [unitQueryKeys.unitsByDocument, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/units/document/${documentSuperId}`).catch((err: { status: number }) => {
        if (err.status === 404) {
          return null;
        }
        throw err;
      });
      return (res?.data as Unit[]) || [];
    },
    {
      enabled: !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export function useCreateUnit(): UseMutationResult<Unit, unknown, CreateUnitInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async (unit: CreateUnitInput) => {
      const res = await request.post('/gaby/units/', unit);
      return res?.data as Unit;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([unitQueryKeys.unitsByDocument, variables.documentSuperId]);
      },
    }
  );
}

export function useUpdateUnit(): UseMutationResult<SuccessResponse, unknown, UpdateUnitInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ unitId, ...unitData }: UpdateUnitInput) => {
      const res = await request.put(`/gaby/units/${unitId}`, unitData);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([unitQueryKeys.unit, variables.unitId]);
        if (variables.documentSuperId) {
          queryClient.invalidateQueries([unitQueryKeys.unitsByDocument, variables.documentSuperId]);
        }
      },
    }
  );
}

export function useDeleteUnit(): UseMutationResult<SuccessResponse, unknown, DeleteUnitInput> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ unitId }: DeleteUnitInput) => {
      const res = await request.del(`/gaby/units/${unitId}`);
      return res?.data as SuccessResponse;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([unitQueryKeys.unit, variables.unitId]);
        if (variables.documentSuperId) {
          queryClient.invalidateQueries([unitQueryKeys.unitsByDocument, variables.documentSuperId]);
        }
      },
    }
  );
}
